import { useEffect, useMemo, useState } from "react";
import { sanitizeHtml } from "../../lib/sanitize";

/**
 * Shared template-picker used both on Kanban cards and in the lead drawer.
 *
 * Props:
 *   templates: [{id, name, subject, html}]  — the master list
 *   activities: [{kind, payload, created_at}]  — lead's activity feed
 *                (used to compute "sent X times · last DD.MM.YY")
 *   onPick(template): callback fired when the user selects a template
 *   onClose(): fired when clicking outside the popover
 *   className: optional wrapper class for positioning
 */
export default function TemplatePicker({
    templates, activities = [], onPick, onClose, className = "",
}) {
    const [hoverId, setHoverId] = useState(null);

    // Compute sent-summary per template from the activity feed.
    const summary = useMemo(() => {
        const map = {};
        for (const a of activities || []) {
            if (a.kind !== "email_sent") continue;
            const tid = a.payload?.template_id;
            if (!tid) continue;
            if (!map[tid]) map[tid] = { count: 0, last_at: null };
            map[tid].count += 1;
            if (!map[tid].last_at || a.created_at > map[tid].last_at) {
                map[tid].last_at = a.created_at;
            }
        }
        return map;
    }, [activities]);

    // Close on outside click. Deferred to next tick so the toggle click
    // that opened this doesn't immediately close it.
    useEffect(() => {
        const onDoc = (e) => {
            if (e.target.closest?.("[data-testid='crm-template-picker']")) return;
            onClose?.();
        };
        const t = setTimeout(() => document.addEventListener("mousedown", onDoc), 0);
        return () => { clearTimeout(t); document.removeEventListener("mousedown", onDoc); };
    }, [onClose]);

    const hovered = hoverId ? templates.find((t) => t.id === hoverId) : null;

    return (
        <div
            data-testid="crm-template-picker"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
            className={`bg-white border border-slate-200 rounded-lg shadow-xl overflow-hidden flex ${className}`}
        >
            {/* Left column — list */}
            <div className="w-64 flex-shrink-0">
                <div className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500 border-b border-slate-100">
                    Vælg skabelon
                </div>
                <div className="max-h-72 overflow-y-auto">
                    {templates.map((t) => {
                        const s = summary[t.id];
                        return (
                            <button
                                key={t.id}
                                type="button"
                                onClick={() => onPick(t)}
                                onMouseEnter={() => setHoverId(t.id)}
                                onFocus={() => setHoverId(t.id)}
                                data-testid={`crm-picker-template-${t.id}`}
                                className={`w-full text-left px-3 py-2 border-b border-slate-50 last:border-b-0 transition-colors ${
                                    hoverId === t.id ? "bg-sky-50" : "hover:bg-sky-50"
                                }`}
                            >
                                <div className="flex items-center justify-between gap-2">
                                    <div className="text-sm font-semibold text-slate-900 truncate">
                                        {t.name}
                                    </div>
                                    {s && (
                                        <span
                                            title={`Sidst sendt ${new Date(s.last_at).toLocaleString("da-DK", {dateStyle:"short",timeStyle:"short"})}`}
                                            className="flex-shrink-0 inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider bg-amber-100 text-amber-700 rounded px-1.5 py-0.5">
                                            Sendt ×{s.count}
                                        </span>
                                    )}
                                </div>
                                <div className="text-xs text-slate-500 truncate">{t.subject}</div>
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Right column — preview (only when hovering) */}
            {hovered && (
                <div className="w-80 flex-shrink-0 border-l border-slate-100 bg-slate-50/50">
                    <div className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500 border-b border-slate-100">
                        Forhåndsvisning
                    </div>
                    <div className="p-3 max-h-72 overflow-y-auto">
                        <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-0.5">Emne</div>
                        <div className="text-sm font-semibold text-slate-900 mb-3">{hovered.subject}</div>
                        <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-0.5">Indhold</div>
                        <div
                            className="text-xs text-slate-700 prose prose-xs max-w-none [&_p]:my-1"
                            dangerouslySetInnerHTML={{ __html: sanitizeHtml(hovered.html) }}
                        />
                    </div>
                </div>
            )}
        </div>
    );
}


/**
 * Compact "last N sent" strip — used in the drawer's Send mail section so
 * the rep can eyeball what's already been sent without opening the picker.
 */
export function SentTemplatesLog({ activities, max = 5 }) {
    const items = useMemo(() => {
        const list = (activities || [])
            .filter((a) => a.kind === "email_sent" && a.payload?.template_name)
            .map((a) => ({
                name: a.payload.template_name,
                at: a.created_at,
            }))
            .slice(0, max);
        return list;
    }, [activities, max]);

    if (items.length === 0) return null;
    return (
        <div className="rounded-lg bg-slate-50 border border-slate-200 p-2.5 mb-3"
             data-testid="crm-drawer-sent-log">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1.5">
                Sidste {items.length} sendt
            </div>
            <div className="space-y-1">
                {items.map((it, i) => (
                    <div key={i} className="flex items-center justify-between gap-2 text-xs">
                        <div className="font-medium text-slate-800 truncate">{it.name}</div>
                        <div className="text-slate-500 flex-shrink-0">
                            {new Date(it.at).toLocaleString("da-DK", { dateStyle: "short", timeStyle: "short" })}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
